import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Minimal async-state hook. Avoids pulling in TanStack Query for a wizard with
 * ~4 fetches. Handles loading/error/data states + manual refetch.
 *
 * The fetcher receives an AbortSignal so callers can wire cancellation (we
 * cancel in-flight fetches on unmount via the cleanup).
 */
export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useAsync<T>(
  fetcher: (signal: AbortSignal) => Promise<T>,
  deps: unknown[] = []
): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [tick, setTick] = useState(0);
  // Pin the fetcher so we don't re-run when the parent re-renders with a new
  // inline closure (we only re-run on explicit dep change or refetch).
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const run = useCallback((signal: AbortSignal) => {
    setLoading(true);
    fetcherRef
      .current(signal)
      .then((d) => {
        if (!signal.aborted) {
          setData(d);
          setError(null);
        }
      })
      .catch((e: unknown) => {
        if (!signal.aborted) setError(e instanceof Error ? e : new Error(String(e)));
      })
      .finally(() => {
        if (!signal.aborted) setLoading(false);
      });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    run(controller.signal);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  return { data, loading, error, refetch };
}
